import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { getCredenciales, deleteCredencial, getCredencialSecret } from "../services/api";
import PinModal from "../components/PinModal";
import { registrarVisualizacion } from "../services/auditoria";
import LoaderPremiumLogo from "../components/LoaderPremiumLogo";

const MASK = "••••••••••••••••";

function isProbablyUrl(v) {
  const s = (v ?? "").toString().trim();
  if (!s) return false;
  if (/^https?:\/\/\S+/i.test(s)) return true;
  if (/^[a-z0-9.-]+\.[a-z]{2,}(\/\S*)?$/i.test(s)) return true;
  return false;
}

function normalizeUrl(v) {
  const s = (v ?? "").toString().trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (isProbablyUrl(s)) return `https://${s}`;
  return s;
}

// Detecta “crítico” (ideal: backend envía c.sensitivity="CRITICAL")
function isCriticalCred(c) {
  const s = (c?.sensitivity || c?.criticidad || "").toString().toUpperCase();
  if (s === "CRITICAL" || s === "CRITICA" || s === "CRÍTICA") return true;
  const name = `${c?.servicio || ""} ${c?.categoria || ""} ${c?.notas || ""}`.toLowerCase();
  return name.includes("root") || name.includes("aws root") || name.includes("cuenta principal");
}

function maskEmailSoft(email) {
  const s = (email ?? "").toString().trim();
  if (!s.includes("@")) return s ? "********" : "—";
  const [user, domain] = s.split("@");

  const u =
    user.length <= 2 ? `${user[0] || "*"}*` : `${user.slice(0, 2)}***${user.slice(-1) || "*"}`;

  const parts = domain.split(".");
  const d0 = parts[0] || "";
  const d =
    d0.length <= 2 ? `${d0[0] || "*"}*` : `${d0.slice(0, 2)}***${d0.slice(-1) || "*"}`;
  const rest = parts.length > 1 ? `.${parts.slice(1).join(".")}` : "";

  return `${u}@${d}${rest}`;
}

function maskEmailHard(email) {
  const s = (email ?? "").toString().trim();
  if (!s.includes("@")) return s ? "********" : "—";
  const [, domain] = s.split("@");
  const parts = domain.split(".");
  const d0 = parts[0] || "";
  const rest = parts.length > 1 ? `.${parts.slice(1).join(".")}` : "";
  const d = d0 ? `${d0[0]}***` : "***";
  return `oculto@${d}${rest}`;
}

/** Minimal inline SVG icons (professional, no emojis) */
const Icon = {
  Search: (props) => (
    <svg viewBox="0 0 24 24" fill="none" className={props.className} aria-hidden="true">
      <path
        d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M16.2 16.2 21 21"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  ),
  Copy: (props) => (
    <svg viewBox="0 0 24 24" fill="none" className={props.className} aria-hidden="true">
      <path
        d="M9 9h10v12H9V9Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  ),
  Link: (props) => (
    <svg viewBox="0 0 24 24" fill="none" className={props.className} aria-hidden="true">
      <path
        d="M10 13a5 5 0 0 1 0-7l1.4-1.4a5 5 0 0 1 7 7L17 12"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M14 11a5 5 0 0 1 0 7L12.6 19.4a5 5 0 0 1-7-7L7 12"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  ),
  Plus: (props) => (
    <svg viewBox="0 0 24 24" fill="none" className={props.className} aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  Pencil: (props) => (
    <svg viewBox="0 0 24 24" fill="none" className={props.className} aria-hidden="true">
      <path
        d="M4 20h4l10.5-10.5a2 2 0 0 0 0-2.8l-.2-.2a2 2 0 0 0-2.8 0L5 16v4Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M13.5 6.5 17.5 10.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  ),
  Trash: (props) => (
    <svg viewBox="0 0 24 24" fill="none" className={props.className} aria-hidden="true">
      <path
        d="M6 7h12M10 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2M8 7l1 14h6l1-14"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  ),
  Shield: (props) => (
    <svg viewBox="0 0 24 24" fill="none" className={props.className} aria-hidden="true">
      <path
        d="M12 3 20 7v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V7l8-4Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

export default function CredencialesView() {
  const DEBUG_CREDENCIALES = false;

  const [credenciales, setCredenciales] = useState([]);
  const [credencialActual, setCredencialActual] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);

  const [search, setSearch] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState("Todas");
  const [loading, setLoading] = useState(true);

  // Ideal: esto debería venir de roles/claims, se mantiene por tu flujo actual
  const [modoOperador, setModoOperador] = useState(false); // default admin ON según tu screenshot

  const [toast, setToast] = useState({ show: false, msg: "" });
  const toastTimerRef = useRef(null);

  // type: "copyPass" | "copyAll"
  const pendingAfterPinRef = useRef({ type: "copyPass" });

  // cache temporal del password (copy-only)
  const passCacheRef = useRef({});
  const passTimersRef = useRef({});

  const lastCopiedRef = useRef({ text: "", at: 0 });

  const [debugInfo, setDebugInfo] = useState({
    total: 0,
    withoutAny: 0,
  });

  const showToast = (msg = "Listo") => {
    setToast({ show: true, msg });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast({ show: false, msg: "" }), 1600);
  };

  const copiar = async (texto, toastMsg = "Copiado") => {
    try {
      if (!texto) return showToast("Nada para copiar");
      await navigator.clipboard.writeText(texto);
      lastCopiedRef.current = { text: texto, at: Date.now() };
      showToast(toastMsg);

      setTimeout(async () => {
        try {
          const stillSame =
            lastCopiedRef.current.text === texto && Date.now() - lastCopiedRef.current.at >= 29000;
          if (stillSame) await navigator.clipboard.writeText("");
        } catch {}
      }, 30000);
    } catch (e) {
      console.error("No se pudo copiar:", e);
      showToast("No se pudo copiar");
    }
  };

  const limpiarSecrets = () => {
    Object.values(passTimersRef.current).forEach((t) => clearTimeout(t));
    passTimersRef.current = {};
    passCacheRef.current = {};
  };

  useEffect(() => {
    cargar();

    const onVisibility = () => document.hidden && limpiarSecrets();
    const onBlur = () => limpiarSecrets();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      limpiarSecrets();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    limpiarSecrets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriaFiltro, search, modoOperador]);

  const cargar = async () => {
    try {
      setLoading(true);
      const res = await getCredenciales();
      const raw = res?.data ?? res;

      let normalized = raw;
      if (normalized && typeof normalized === "object" && typeof normalized.body === "string") {
        try {
          normalized = JSON.parse(normalized.body);
        } catch {
          normalized = {};
        }
      }
      if (typeof normalized === "string") {
        try {
          normalized = JSON.parse(normalized);
        } catch {
          normalized = {};
        }
      }

      const items = Array.isArray(normalized)
        ? normalized
        : Array.isArray(normalized?.items)
        ? normalized.items
        : Array.isArray(normalized?.data)
        ? normalized.data
        : [];

      setCredenciales(items);

      if (DEBUG_CREDENCIALES) {
        const withoutAny = items.filter((x) => !x?.hasSecret).length;

        setDebugInfo({ total: items.length, withoutAny });
      }
    } catch (e) {
      console.error("Error cargando credenciales:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchPasswordFromApi = async (id) => {
    const res = await getCredencialSecret(id);
    const raw = res?.data ?? res;

    let normalized = raw;
    if (normalized && typeof normalized === "object" && typeof normalized.body === "string") {
      try {
        normalized = JSON.parse(normalized.body);
      } catch {
        normalized = {};
      }
    }

    if (!normalized?.ok) throw new Error(normalized?.error || normalized?.detail || "No se pudo obtener secreto");
    return (normalized?.password || normalized?.value || "").toString().trim();
  };

  const cachePasswordTemporal = (id, pass, ms = 8000) => {
    passCacheRef.current[id] = (pass || "").trim();
    if (passTimersRef.current[id]) {
      clearTimeout(passTimersRef.current[id]);
      delete passTimersRef.current[id];
    }
    passTimersRef.current[id] = setTimeout(() => {
      delete passCacheRef.current[id];
      delete passTimersRef.current[id];
    }, ms);
  };

  const solicitarPIN = (cred, type) => {
    setCredencialActual(cred);
    pendingAfterPinRef.current = { type };
    setShowPinModal(true);
  };

  const construirTextoCompleto = (cred, email, pass) => {
    const partes = [
      `Servicio: ${cred?.servicio || "—"}`,
      `Email/Usuario: ${email || "—"}`,
      `Password: ${pass || "—"}`,
      cred?.url ? `URL: ${cred.url}` : null,
      cred?.categoria ? `Categoria: ${cred.categoria}` : null,
      cred?.notas ? `Notas: ${cred.notas}` : null,
    ].filter(Boolean);

    return partes.join("\n");
  };

  const copiarEmail = async (cred) => {
    if (!cred) return;

    const critical = isCriticalCred(cred);

    if (modoOperador && critical) {
      showToast("Acceso restringido");
      return;
    }

    const email = (cred?.usuario || "").toString().trim();
    if (!email) {
      showToast("No hay email/usuario");
      return;
    }

    await copiar(email, "Email copiado");
  };

  const copiarPassword = async (cred) => {
    if (!cred) return;

    const hasSecret = cred?.hasSecret === true;

    if (!hasSecret) return showToast("Sin secreto configurado");
    if (modoOperador) return showToast("Acceso restringido");

    const cached = passCacheRef.current[cred.id] || "";
    if (cached) return copiar(cached, "Password copiado");

    solicitarPIN(cred, "copyPass");
  };

  const copiarTodo = async (cred) => {
    if (!cred) return;

    const hasSecret = cred?.hasSecret === true;

    if (!hasSecret) return showToast("Sin secreto configurado");
    if (modoOperador) return showToast("Acceso restringido");

    const email = (cred?.usuario || "").toString().trim();
    const pass = passCacheRef.current[cred.id] || "";

    if (!pass) {
      solicitarPIN(cred, "copyAll");
      return;
    }

    const texto = construirTextoCompleto(cred, email, pass);
    await copiar(texto, "Datos copiados");
  };

  const eliminar = async (id) => {
    if (modoOperador) return showToast("Acceso restringido");
    if (!window.confirm("¿Deseas eliminar esta credencial?")) return;

    try {
      await deleteCredencial(id);
      showToast("Eliminado");
      cargar();
    } catch (e) {
      console.error("Error eliminando credencial:", e);
      showToast("No se pudo eliminar");
    }
  };

  const categorias = useMemo(() => {
    const cats = Array.from(
      new Set((credenciales || []).map((c) => c?.categoria).filter((x) => x && x.trim() !== ""))
    );
    return ["Todas", ...cats];
  }, [credenciales]);

  const listaFiltrada = useMemo(() => {
    const texto = search.toLowerCase().trim();

    return (credenciales || []).filter((c) => {
      if (!c) return false;
      if (categoriaFiltro !== "Todas" && (c.categoria || "") !== categoriaFiltro) return false;
      if (!texto) return true;

      const safeUser = isCriticalCred(c) ? "" : (c.usuario || "");
      const blob = [c.servicio, safeUser, c.url, c.notas, c.categoria].filter(Boolean).join(" ").toLowerCase();
      return blob.includes(texto);
    });
  }, [credenciales, search, categoriaFiltro]);

  if (loading) return <LoaderPremiumLogo />;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-5 py-8 space-y-5">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Credenciales</h1>
              <span className="hidden sm:inline-flex items-center gap-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-full px-3 py-1">
                <Icon.Shield className="h-4 w-4" />
                Vault
              </span>
            </div>

            

            {DEBUG_CREDENCIALES && (
              <p className="text-[12px] text-slate-500 mt-1">
                Debug: total={debugInfo.total} · sinSecret={debugInfo.withoutAny}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setModoOperador((v) => !v)}
              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                ${
                  modoOperador
                    ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-800"
                    : "bg-white text-slate-900 border-slate-200 hover:bg-slate-50"
                }`}
            >
              {modoOperador ? "Operador" : "Admin"}
            </button>

            {!modoOperador && (
              <Link
                to="/sitios/nuevo"
                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700
                  focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <Icon.Plus className="h-4 w-4" />
                Nueva
              </Link>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-center">
            <div className="md:col-span-9">
              <div className="relative">
                <Icon.Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Buscar por servicio, notas, URL o categoría"
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-900
                    placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="md:col-span-3">
              <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Categoría</label>
              <select
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-3 bg-white
                  focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={categoriaFiltro}
                onChange={(e) => setCategoriaFiltro(e.target.value)}
              >
                {categorias.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* List */}
        {listaFiltrada.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
            <div className="text-sm font-medium text-slate-700">Sin resultados</div>
            <div className="text-xs text-slate-500 mt-1">Ajusta la búsqueda o la categoría.</div>
          </div>
        ) : (
          <div className="space-y-4">
            {listaFiltrada.map((c) => {
              const hasSecret = c?.hasSecret === true;

              const critical = isCriticalCred(c);

              const urlOk = isProbablyUrl(c.url);
              const urlHref = urlOk ? normalizeUrl(c.url) : "";

              const emailRaw = (c?.usuario || "").toString().trim();
              const emailMasked = critical ? maskEmailHard(emailRaw) : maskEmailSoft(emailRaw);

              return (
                <div
                  key={c.id}
                  className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition"
                >
                  <div className="p-5">
                    {/* Title row */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-slate-900 truncate">
                            {c.servicio || "Servicio sin nombre"}
                          </h3>

                          {critical && (
                            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800">
                              Crítico
                            </span>
                          )}

                          {!hasSecret && (
                            <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
                              Sin secreto
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-slate-500 mt-1 line-clamp-1">
                          {c.notas || "Sin notas"}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copiarTodo(c)}
                          disabled={!hasSecret || modoOperador}
                          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white
                            hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed
                            focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          title={modoOperador ? "Acceso restringido" : "Copiar todo (PIN si falta password)"}
                        >
                          <Icon.Copy className="h-4 w-4" />
                          Copiar todo
                        </button>
                      </div>
                    </div>

                    {/* Fields */}
                    <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-12">
                      {/* Email */}
                      <div className="lg:col-span-4">
                        <div className="text-[11px] font-semibold text-slate-500 uppercase">Usuario / Email</div>
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 truncate">
                            {emailRaw ? emailMasked : "—"}
                          </div>

                          <button
                            onClick={() => copiarEmail(c)}
                            disabled={critical && modoOperador}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900
                              hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed
                              focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            title={critical && modoOperador ? "Acceso restringido" : "Copiar email"}
                          >
                            <Icon.Copy className="h-4 w-4" />
                            Email
                          </button>
                        </div>
                      </div>

                      {/* Password */}
                      <div className="lg:col-span-4">
                        <div className="text-[11px] font-semibold text-slate-500 uppercase">Password</div>
                        <div className="mt-2 flex items-center gap-2">
                          <div
                            className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 font-mono"
                            style={{ userSelect: "none" }}
                          >
                            {MASK}
                          </div>

                          <button
                            onClick={() => copiarPassword(c)}
                            disabled={!hasSecret || modoOperador}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900
                              hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed
                              focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            title={modoOperador ? "Acceso restringido" : "Copiar password (PIN)"}
                          >
                            <Icon.Copy className="h-4 w-4" />
                            Password
                          </button>
                        </div>

                        
                      </div>

                      {/* URL */}
                      <div className="lg:col-span-4">
                        <div className="text-[11px] font-semibold text-slate-500 uppercase">URL</div>
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 truncate">
                            {c.url || "—"}
                          </div>

                          {c.url && urlOk && (
                            <a
                              href={urlHref}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900
                                hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                              title="Abrir URL"
                            >
                              <Icon.Link className="h-4 w-4" />
                              Abrir
                            </a>
                          )}

                          {c.url && (
                            <button
                              onClick={() => copiar(c.url, "URL copiada")}
                              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900
                                hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                              title="Copiar URL"
                            >
                              <Icon.Copy className="h-4 w-4" />
                              Copiar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Footer actions */}
                    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-slate-100 pt-4">
                      

                      {!modoOperador && (
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/sitios/editar/${c.id}`}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900
                              hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          >
                            <Icon.Pencil className="h-4 w-4" />
                            Editar
                          </Link>

                          <button
                            onClick={() => eliminar(c.id)}
                            className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700
                              hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:ring-offset-2"
                          >
                            <Icon.Trash className="h-4 w-4" />
                            Eliminar
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Critical note */}
                    {critical && (
                      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-900">
                        Credencial crítica: datos enmascarados. Recomendado: restringir acciones críticas por rol en backend.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Toast */}
      <div
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-200 ${
          toast.show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="rounded-xl bg-slate-900/95 px-4 py-2 text-sm font-medium text-white shadow-lg ring-1 ring-white/10">
          {toast.msg}
        </div>
      </div>

      {/* PIN Modal */}
      {showPinModal && (
        <PinModal
          onClose={() => {
            pendingAfterPinRef.current = { type: "copyPass" };
            setShowPinModal(false);
          }}
          onSuccess={async () => {
            const cred = credencialActual;
            const pending = pendingAfterPinRef.current?.type || "copyPass";

            try {
              registrarVisualizacion(cred.id, cred.servicio);

              const pass = await fetchPasswordFromApi(cred.id);
              cachePasswordTemporal(cred.id, pass, 8000);

              if (pending === "copyPass") {
                await copiar(pass, "Password copiado");
              }

              if (pending === "copyAll") {
                const email = (cred?.usuario || "").toString().trim();
                const texto = construirTextoCompleto(cred, email, pass);
                await copiar(texto, "Datos copiados");
              }
            } catch (e) {
              console.error("Error trayendo /secret:", e);
              showToast("No se pudo obtener password");
            } finally {
              pendingAfterPinRef.current = { type: "copyPass" };
              setShowPinModal(false);
            }
          }}
        />
      )}
    </div>
  );
}
